import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import {
  useInsertDocumentAssessmentResult,
  useUpdateDocumentAssessmentResult,
} from '@/hooks/mutations/assessment-result';
import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetDocumentAssessmentResultById } from '@/hooks/queries';
import { assessmentResultsUrl } from '@/utils/urls';

import DocumentAssessmentResultForm from './DocumentAssessmentResultForm';
import type { DocumentAssessmentResultFormDataFields } from './documentAssessmentResultSchema';
import { defaultValues } from './documentAssessmentResultSchema';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  documentIds?: string[];
  header?: string;
};

const ConnectedDocumentAssessmentResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  documentIds,
  header,
}) => {
  const navigate = useNavigate();

  const { data } = useGetDocumentAssessmentResultById({
    queryArgs: { id: id ?? '' },
    shouldSkip: !id,
  });
  const documentAssessmentResult = data?.document_assessment_result?.[0];
  const {
    hasPermission: canUpdateDocumentAssessmentResult,
    loading: isLoadingUpdateDocumentAssessmentResult,
  } = useHasPermissionQuery('update:document_assessment_result');

  const { updateFiles } = useMultiParentFileUpdate();
  const { insertDocumentAssessmentResult } =
    useInsertDocumentAssessmentResult();
  const { updateDocumentAssessmentResult } =
    useUpdateDocumentAssessmentResult();

  documentIds = documentIds ?? [];
  const parentDocument = documentAssessmentResult?.parents.find(
    (p) => p.document
  );
  if (parentDocument?.document?.Id) {
    documentIds.push(parentDocument.document.Id);
  } else if (assessedItem?.Id) {
    documentIds.push(assessedItem.Id);
  }

  const onSave = async (values: DocumentAssessmentResultFormDataFields) => {
    const { files } = values;
    const documentAssessmentResultIds: string[] = [];
    if (documentAssessmentResult) {
      const result = await updateDocumentAssessmentResult({
        Id: documentAssessmentResult.Id,
        // Optimistic-concurrency token: the snapshot this form was loaded from.
        // A second editor saving over a newer rating is rejected with a 409
        // rather than silently winning (COI-622).
        OriginalTimestamp: documentAssessmentResult.ModifiedAtTimestamp,
        Rating: values.Rating,
        Rationale: values.Rationale ?? null,
        TestDate: values.TestDate ?? null,
        CustomAttributeData: values.CustomAttributeData ?? null,
      });

      if (result.update_document_assessment_result?.affected_rows === 0) {
        throw new Error('Document assessment result update failed');
      }
      documentAssessmentResultIds.push(id!);
    } else {
      const result = await insertDocumentAssessmentResult({
        Rating: values.Rating,
        Rationale: values.Rationale ?? null,
        TestDate: values.TestDate ?? null,
        CustomAttributeData: values.CustomAttributeData ?? null,
        AssessmentId: parentId,
        DocumentIds: (values.DocumentIds ?? []).map((r) => r.value),
      });

      if (!result.insertChildDocumentAssessmentResult?.Ids?.length) {
        throw new Error('Document assessment result id is missing');
      }
      documentAssessmentResultIds.push(
        ...result.insertChildDocumentAssessmentResult.Ids
      );
    }

    await updateFiles({
      parentIds: documentAssessmentResultIds,
      parentType: Parent_Type_Enum.DocumentAssessmentResult,
      selectedFiles: files,
      originalFiles: documentAssessmentResult?.files.map((f) => f.file) ?? [],
    });
    if (navigateToResults && parentId) {
      navigate(assessmentResultsUrl(parentId));
    }
  };

  return (
    <DocumentAssessmentResultForm
      header={header}
      defaultValues={{
        ...defaultValues,
        DocumentIds: documentIds.map((c) => ({ value: c })),
        AssessmentId: parentId ?? null,
      }}
      values={
        documentAssessmentResult
          ? {
              Rating: documentAssessmentResult?.Rating ?? 1,
              Rationale: documentAssessmentResult?.Rationale ?? '',
              AssessmentId:
                documentAssessmentResult?.parents.find((p) => p.assessment)
                  ?.assessment?.Id ?? null,
              DocumentIds: documentIds.map((c) => ({ value: c })),
              TestDate: documentAssessmentResult?.TestDate,
              CustomAttributeData:
                documentAssessmentResult?.CustomAttributeData,
              files: documentAssessmentResult?.files.map((f) => f.file) ?? [],
            }
          : undefined
      }
      onDismiss={onDismiss}
      onSave={onSave}
      readOnly={
        readonly &&
        (isLoadingUpdateDocumentAssessmentResult ||
          !canUpdateDocumentAssessmentResult)
      }
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableDocumentSelector={id != undefined}
      beforeFieldsSlot={beforeFieldsSlot}
      showSelector={showAssessmentSelector ? 'rating' : undefined}
      assessmentMode={'rating'}
    />
  );
};

export default ConnectedDocumentAssessmentResultForm;
